/**
 * Resolve an asset reference to a renderable URL.
 *
 * The behavior splits on whether NEXT_PUBLIC_CDN_URL is configured:
 *
 * 1. **Dev / no CDN** — returns `/<path>`, served from Next's static handler
 *    out of the `public/` directory. The asset manifest is ignored.
 * 2. **Production / CDN configured** — looks up the path in
 *    `lib/asset-manifest.json` (populated by `scripts/sync-assets.ts`). If
 *    a hashed entry exists, the CDN URL points at it; otherwise it falls
 *    back to the bare path. This means new assets dropped into `public/`
 *    still resolve (just without content-hashed caching) until the next
 *    sync runs.
 *
 * Absolute URLs (http://, https://, data:) pass through unchanged so
 * Unsplash / data-URI references in the codebase keep working.
 *
 * @example
 *   assetUrl('platforms/snapp.svg')
 *   // dev: '/platforms/snapp.svg'
 *   // prod: 'https://cdn.trend.example/platforms/snapp.a3f8b2c1.svg'
 */
import manifestJson from './asset-manifest.json'

type ManifestEntry = {
  url: string
  variants?: { webp?: string; avif?: string }
  contentType?: string
  size?: number
  hash?: string
}

type Manifest = {
  version: number
  generatedAt: string | null
  assets: Record<string, ManifestEntry>
}

const manifest = manifestJson as Manifest

/** Set by `npm run assets:sync` (or your prod deploy) to enable CDN routing. */
const CDN_URL = process.env.NEXT_PUBLIC_CDN_URL?.replace(/\/$/, '') ?? ''

function isAbsolute(path: string): boolean {
  return /^(?:https?:)?\/\//.test(path) || path.startsWith('data:')
}

function normalize(path: string): string {
  return path.startsWith('/') ? path.slice(1) : path
}

export function assetUrl(path: string): string {
  if (isAbsolute(path)) return path

  const key = normalize(path)

  if (!CDN_URL) {
    return `/${key}`
  }

  const entry = manifest.assets[key]
  const resolved = entry?.url ?? key
  return `${CDN_URL}/${resolved}`
}

function variantUrl(path: string, variant: 'avif' | 'webp'): string | null {
  if (isAbsolute(path) || !CDN_URL) return null
  const entry = manifest.assets[normalize(path)]
  const v = entry?.variants?.[variant]
  return v ? `${CDN_URL}/${v}` : null
}

/**
 * Return the AVIF variant URL if available, otherwise null. Useful for
 * <picture><source type="image/avif" srcSet={...} /></picture> patterns.
 */
export function assetAvifUrl(path: string): string | null {
  return variantUrl(path, 'avif')
}

/** Same as `assetAvifUrl` but for WebP. */
export function assetWebpUrl(path: string): string | null {
  return variantUrl(path, 'webp')
}
